/** Copyright 2025 Midas Healthcare Solutions - All Rights Reserved **/
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService, Organization, UiActions } from '@midas/shared/common';
import { TranslocoService } from '@ngneat/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { fetch, pessimisticUpdate } from '@nrwl/angular';
import { distinctUntilChanged, map, mergeMap } from 'rxjs';
import { UserActions } from '../user';
import * as CurrentOrgActions from './current-org.actions';

@Injectable()
export class CurrentOrgEffects {
  readonly getCurrentOrg$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CurrentOrgActions.getCurrentOrg),
      fetch({
        id: () => true,
        run: ({ organizationName }) => {
          return this.api
            .get<Organization>('/organization/current', undefined, { 'X-Organization': organizationName })
            .pipe(
              map((org) => {
                return CurrentOrgActions.getCurrentOrgSuccess({ org });
              }),
            );
        },
        onError: (action, error) => {
          return CurrentOrgActions.getCurrentOrgError({ error, showInDialog: true });
        },
      }),
    ),
  );

  readonly updateCurrentOrg$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CurrentOrgActions.updateCurrentOrg),
      pessimisticUpdate({
        run: (dto) =>
          this.api.put<Organization>('/organization', dto).pipe(
            map((org) => {
              const newUrl = this.router.url.replace(/\/org\/(.*?)\//, `/org/${org.name}/`);
              this.router.navigateByUrl(newUrl);
              this.store.dispatch(UserActions.refreshProfile());
              this.store.dispatch(
                UiActions.setGlobalSuccess({
                  success: this.transloco.translate('Organization updated successfully'),
                }),
              );
              return CurrentOrgActions.updateCurrentOrgSuccess({ org });
            }),
          ),
        onError: (action, error) => {
          return CurrentOrgActions.updateCurrentOrgError({ error });
        },
      }),
    ),
  );

  readonly updateCurrentOrgCollectionBaseline$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CurrentOrgActions.updateCurrentOrgCollectionBaseline),
      pessimisticUpdate({
        run: (dto) =>
          this.api.put<Organization>('/organization/collection-baseline', dto).pipe(
            map((org) => {
              this.store.dispatch(
                UiActions.setGlobalSuccess({
                  success: this.transloco.translate(
                    'Organization Baseline <i>Rx</i>Intercept\u2122 Collection Rate updated successfully',
                  ),
                }),
              );
              return CurrentOrgActions.updateCurrentOrgCollectionBaselineSuccess({ org });
            }),
          ),
        onError: (action, error) => {
          return CurrentOrgActions.updateCurrentOrgCollectionBaselineError({ error });
        },
      }),
    ),
  );

  reloadProfileAfterSetCurrentOrg$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CurrentOrgActions.setCurrentOrg),
      distinctUntilChanged((a, b) => a.organizationName === b.organizationName),
      mergeMap(({ organizationName }) => [
        CurrentOrgActions.getCurrentOrg({ organizationName }),
        UserActions.getProfile(),
      ]),
    ),
  );

  readonly sendOrganizationUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CurrentOrgActions.sendOrganizationUpdate),
      fetch({
        id: () => true,
        run: ({ orgId }) =>
          this.api.post<void>('/test/send-update/organization', { organizationId: orgId }, {}).pipe(
            map(() => {
              this.store.dispatch(
                UiActions.setGlobalSuccess({
                  success: this.transloco.translate('Organization Update sent successfully'),
                }),
              );
              return CurrentOrgActions.sendOrganizationUpdateSuccess();
            }),
          ),
        onError: (action, error) => {
          return CurrentOrgActions.sendOrganizationUpdateError({ error });
        },
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private api: ApiService,
    private router: Router,
    private store: Store,
    private transloco: TranslocoService,
  ) {}
}
